'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { OrganisationSwitchWarningDialog } from '@workspace/ui';
import { useOrganisationSwitchConfirmation } from '@workspace/ui/hooks';
import { cn } from '@workspace/ui/lib';
import { ChevronsUpDown, LoaderCircle } from 'lucide-react';
import { useCanShowMultiOrganisationSwitcher } from '@/hooks/use-can-show-multi-organisation-switcher';
import { useSwitchOrganisationItem } from '@/hooks/use-switch-organisation-item';
import { useUserVerification } from '@/hooks/use-user';
import { getActiveOrganisationFromUser } from '@/lib/active-organisation';
import {
  ORGANISATION_SWITCHER_SECTION_LABEL,
  buildOrganisationSwitcherItems,
  getOrganisationSwitchViewName,
  isOrganisationSwitcherItemActive,
} from '@/lib/organisation-switcher-items';
import { OrganisationSwitcherAvatar } from '@/components/layout/organisation-switcher-avatar';
import { useAuthStore } from '@/stores/authStore';

interface MobileOrganisationSwitcherProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export function MobileOrganisationSwitcher({ isOpen, setIsOpen }: MobileOrganisationSwitcherProps) {
  const user = useAuthStore((state) => state.user);

  const userId = user?.user.id;
  const activeOrganisationId = getActiveOrganisationFromUser(user)?.id;
  const canShowSwitcher = useCanShowMultiOrganisationSwitcher();
  const { switchToItem, isSwitching } = useSwitchOrganisationItem({
    onSwitched: () => setIsOpen(false),
  });
  const { pendingItem, requestSwitch, confirmSwitch, isDialogOpen, setDialogOpen } =
    useOrganisationSwitchConfirmation({ switchToItem });

  const { data: userVerification, isLoading } = useUserVerification(!!userId && canShowSwitcher);

  const organisations = userVerification?.organisations ?? [];
  const switcherItems = buildOrganisationSwitcherItems(organisations);
  const activeRoleContext = userVerification?.active_organisation?.role_context;
  const activeOrganisation = organisations.find(
    (organisation) => organisation.id === activeOrganisationId
  );

  if (!canShowSwitcher || switcherItems.length <= 1) return null;

  return (
    <>
      <div className="notranslate flex flex-col" translate="no">
        <button
          type="button"
          className="group hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-10 w-full cursor-pointer items-center gap-2 rounded-lg p-2 text-sm transition-colors"
          aria-label={'Organisation'}
          onClick={() => setIsOpen(!isOpen)}
        >
          <OrganisationSwitcherAvatar size="sm" />
          <p className="text-sidebar-foreground flex-1 text-left">
            {activeOrganisation?.name ?? 'Organisation'}
          </p>
          {isSwitching || isLoading ? (
            <LoaderCircle className="animate-spin" />
          ) : (
            <ChevronsUpDown />
          )}
        </button>

        {isOpen && (
          <div className="bg-grey-25 dark:bg-grey-1000 absolute top-0 left-0 z-10 flex min-h-full w-full flex-col gap-4">
            <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center lg:gap-3">
              <p className="text-primary text-base font-medium dark:text-neutral-100">
                {ORGANISATION_SWITCHER_SECTION_LABEL}
              </p>

              <hr className="border-grey-100 dark:border-grey-800 block w-full lg:hidden" />
            </div>

            <div className="flex flex-col gap-2">
              {switcherItems.map((item) => {
                const isActive = isOrganisationSwitcherItemActive(
                  item,
                  activeOrganisationId,
                  activeRoleContext
                );
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={cn(
                      'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex min-h-12 w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                      isActive && 'bg-sidebar-accent'
                    )}
                    disabled={isActive || isSwitching}
                    onClick={() => {
                      setIsOpen(false);
                      requestSwitch(item);
                    }}
                  >
                    <OrganisationSwitcherAvatar size="md" />
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <p className="text-neutral-200 truncate font-medium dark:text-neutral-100">
                        {item.organisationName}
                      </p>
                      {item.subtitle ? (
                        <p className="text-grey-600 truncate text-xs">{item.subtitle}</p>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <OrganisationSwitchWarningDialog
        open={isDialogOpen}
        onOpenChange={setDialogOpen}
        onConfirm={confirmSwitch}
        viewName={pendingItem ? getOrganisationSwitchViewName(pendingItem) : ''}
        isConfirming={isSwitching}
      />
    </>
  );
}
